"use strict";
/*
 * 8x8 Confidential & Proprietary || Copyright 2025 8x8, Inc. or its affiliates. All Rights Reserved.
 *  _____      _____
 * |  _  |    |  _  |
 *  \ V /__  __\ V /   ___ ___  _ __ ___
 *  / _ \\ \/ // _ \  / __/ _ \| '_ ` _ \
 * | |_| |>  <| |_| || (_| (_) | | | | | |
 * \_____/_/\_\_____(_)___\___/|_| |_| |_|
 *
 * @License https://www.8x8.com/terms-and-conditions/software-development-kit-license-agreement
 * @Author Comanici Paul
 * @Date 2025/08/28
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.arrayBufferToBase64 = arrayBufferToBase64;
exports.decryptData = decryptData;
exports.encryptData = encryptData;
exports.generateEncryptionKey = generateEncryptionKey;
exports.importEncryptionKey = importEncryptionKey;
const tslib_1 = require("tslib");
const dependencies_1 = require("./dependencies");
const observability_codes_1 = require("./observability.codes");
/**
 * Check if crypto.subtle is available (browser environment)
 */
function isCryptoSubtleAvailable() {
    return (typeof crypto !== "undefined" &&
        typeof crypto.subtle !== "undefined" &&
        typeof crypto.subtle.generateKey === "function");
}
/**
 * Generate a new AES-256-GCM encryption key
 */
function generateEncryptionKey(instanceId) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const cryptoSubtleAvailable = isCryptoSubtleAvailable();
        if (!cryptoSubtleAvailable) {
            const error = new Error("Crypto.subtle is not available in this environment");
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_NOT_AVAILABLE },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} ${error.message}`);
        }
        try {
            const key = yield crypto.subtle.generateKey({
                name: "AES-GCM",
                length: 256,
            }, true, // extractable
            ["encrypt", "decrypt"]);
            const keyData = yield crypto.subtle.exportKey("raw", key);
            (0, dependencies_1.pushLog)(instanceId, ["Generated new AES-256-GCM encryption key"], {
                context: {
                    code: observability_codes_1.FILE_CODES.CRYPTO_KEY_GENERATED,
                    keySize: 256,
                },
            });
            return { key, keyData };
        }
        catch (error) {
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_KEY_GENERATION_ERROR },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} Failed to generate encryption key: ${error}`);
        }
    });
}
/**
 * Encrypt data using AES-256-GCM
 */
function encryptData(data, key, instanceId) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const cryptoSubtleAvailable = isCryptoSubtleAvailable();
        if (!cryptoSubtleAvailable) {
            const error = new Error("Crypto.subtle is not available in this environment");
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_NOT_AVAILABLE },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} ${error.message}`);
        }
        try {
            // Generate random IV (12 bytes for GCM)
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encryptedData = yield crypto.subtle.encrypt({
                name: "AES-GCM",
                iv: iv,
            }, key, data);
            (0, dependencies_1.pushLog)(instanceId, ["Data encrypted successfully"], {
                context: {
                    code: observability_codes_1.FILE_CODES.CRYPTO_ENCRYPT_SUCCESS,
                    dataSize: data.byteLength,
                    encryptedSize: encryptedData.byteLength,
                },
            });
            return {
                encryptedData,
                iv: iv.buffer,
            };
        }
        catch (error) {
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_ENCRYPT_ERROR },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} Failed to encrypt data: ${error}`);
        }
    });
}
/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
/**
 * Import AES-256-GCM encryption key from ArrayBuffer key data
 */
function importEncryptionKey(keyData, instanceId) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const cryptoSubtleAvailable = isCryptoSubtleAvailable();
        if (!cryptoSubtleAvailable) {
            const error = new Error("Crypto.subtle is not available in this environment");
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_NOT_AVAILABLE },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} ${error.message}`);
        }
        try {
            const key = yield crypto.subtle.importKey("raw", keyData, {
                name: "AES-GCM",
                length: 256,
            }, false, ["decrypt"]);
            (0, dependencies_1.pushLog)(instanceId, ["AES-256-GCM key imported successfully"], {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_KEY_IMPORTED },
            });
            return key;
        }
        catch (error) {
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_KEY_IMPORT_ERROR },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} Failed to import encryption key: ${error}`);
        }
    });
}
/**
 * Decrypt data using AES-256-GCM
 */
function decryptData(encryptedData, key, iv, instanceId) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const cryptoSubtleAvailable = isCryptoSubtleAvailable();
        if (!cryptoSubtleAvailable) {
            const error = new Error("Crypto.subtle is not available in this environment");
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_NOT_AVAILABLE },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} ${error.message}`);
        }
        try {
            const decryptedData = yield crypto.subtle.decrypt({
                name: "AES-GCM",
                iv: iv,
            }, key, encryptedData);
            (0, dependencies_1.pushLog)(instanceId, ["Data decrypted successfully"], {
                context: {
                    code: observability_codes_1.FILE_CODES.CRYPTO_DECRYPT_SUCCESS,
                    encryptedSize: encryptedData.byteLength,
                    decryptedSize: decryptedData.byteLength,
                },
            });
            return decryptedData;
        }
        catch (error) {
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.CRYPTO_DECRYPT_ERROR },
            });
            throw new Error(`${dependencies_1.ERROR_PREFIX} Failed to decrypt data: ${error}`);
        }
    });
}
//# sourceMappingURL=crypto.js.map